import { type ReactElement, useCallback, useState } from 'react'

import { type ModalProps } from '@DS/components/navigation/modal/Modal'
import { type ModalControls, type ModalNavControls } from '@DS/components/navigation/modal/Modal.types'
import { type WithRequired } from '@Utils/type'

export type UseModalStateReturn = {
  isModalOpen: boolean
  currentPageName?: string
  openModal: (pageName?: string) => void
  modalProps: WithRequired<ModalProps, 'modalNavControls'>
} & ModalControls

/**
 * To be used to control a modal with multiple pages
 */
export const useModal = (): UseModalStateReturn => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [currentPageName, setCurrentPageName] = useState('')
  const [dynamicPage, setDynamicPage] = useState<ReactElement | undefined>()

  // Page is sub page if path pattern is "main/subpage" or "/main/subpage"
  const hasToDisplayBackButton = !!dynamicPage || currentPageName.split('/').filter((e) => e).length > 1

  const openModal = useCallback((pageName: string | undefined = '') => {
    setDynamicPage(undefined)
    setCurrentPageName(pageName)
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
    setDynamicPage(undefined)
  }, [])

  const goToModalPage = (pageName: string) => {
    setDynamicPage(undefined)
    setIsModalOpen(true)
    setCurrentPageName(pageName)
  }

  const goToDynamicModalPage = (dynamicPageEl: ReactElement) => {
    setIsModalOpen(true)
    setDynamicPage(dynamicPageEl)
    setCurrentPageName(`${currentPageName}/dynamic`)
  }

  const goToParentPage = () => {
    if (!hasToDisplayBackButton || !currentPageName) {
      return
    }
    setDynamicPage(undefined)
    const parentPage = currentPageName.substring(0, currentPageName.lastIndexOf('/'))
    setCurrentPageName(parentPage)
  }

  const modalNavControls: ModalNavControls = { goToModalPage, goToParentPage, goToDynamicModalPage }

  return {
    isModalOpen,
    openModal,
    currentPageName,
    closeModal,
    ...modalNavControls,
    modalProps: {
      isOpen: isModalOpen,
      onClose: closeModal,
      dynamicPage,
      isBackIconDisplayed: hasToDisplayBackButton,
      onBackIconClick: goToParentPage,
      modalNavControls,
    },
  }
}
